/**
 * Deciding which CIS customer a Promo Office customer is.
 *
 * Matching links a customer automatically only when its name *and* its
 * postcode agree on exactly one CIS customer. Everything weaker lands here —
 * measured at 57% of customers behind open quotes on first run, then a low
 * steady rate. This is not a queue with an owner: it is a tab on the page
 * everyone already uses, open to anyone who can see a quote.
 *
 * The decision is per *customer*, not per quote. Vetplus has twelve open
 * quotes and resolving it clears all twelve, which is why the rows are
 * customers and the counts are the sort key.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  PromoOfficeCisCustomer,
  PromoOfficeCustomerLinkSummary,
  PromoOfficeCustomerMatch,
  PromoOfficeUnlinkedCustomer,
} from "@shared-core/data/adapter";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import type { NewCustomerForm } from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  bindNewCustomerErrors,
  emptyNewCustomerDraft,
  fieldsNeedAFix,
  newCustomerOwners,
  wontSeeNewCustomer,
  type NewCustomerDraft,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import {
  NewCustomerFields,
  NotInPortfolioNotice,
} from "../customers/NewCustomerFields";
import {
  describeLinkDecision,
  describeMatchedSignals,
  describeOpenWork,
} from "@shared-core/domain/promo-office-matching";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Card } from "../../primitives/card";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../primitives/dialog";
import { ListPagination } from "../../patterns/ListPagination";
import { useCanEditCustomers } from "../../../auth/hooks";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  UserPlus,
  X,
} from "lucide-react";

const PAGE_SIZE = 25;

/**
 * The customer in One column on the quotes and orders tables.
 *
 * Name first, then the code: the name is what somebody recognises, and the code
 * is what they quote back to Tharstern. A code alone is unreadable to anyone
 * who does not already know the customer by it.
 *
 * The name can be missing while the code is not — a customer deleted or merged
 * in CIS since the link was made. The code still shows, because the decision
 * was still real, and a row that fell back to "No customer in One" would invite
 * somebody to link it a second time.
 */
export function CisCustomerCell({
  link,
}: {
  link: PromoOfficeCustomerLinkSummary | null;
}) {
  if (link === null) {
    return (
      <Badge variant="warning" className="font-normal">
        No customer in One
      </Badge>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium">
        {link.cisCustomerName ?? "Not found in One"}
      </span>
      <span className="text-xs text-muted-foreground">
        {/* Each fact is its own element rather than one run of text: the code
            and the provenance are separately meaningful, and a bare text node
            beside a sibling span is addressable by neither a test nor a
            screen reader reading one line at a time. */}
        {link.cisCustomerCode && (
          <>
            <span className="font-mono">{link.cisCustomerCode}</span>
            <span aria-hidden="true"> · </span>
          </>
        )}
        <span>{describeLinkDecision(link.decidedBy)}</span>
      </span>
    </div>
  );
}

type CandidateFilter = "all" | "suggested" | "none";

const FILTER_TO_QUERY: Record<CandidateFilter, boolean | undefined> = {
  all: undefined,
  suggested: true,
  none: false,
};

export function UnlinkedCustomersTab({
  onResolved,
}: {
  /** Lets the page refresh its tab counts once a customer leaves the list. */
  onResolved?: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [customers, setCustomers] = useState<
    PromoOfficeUnlinkedCustomer[] | null
  >(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<CandidateFilter>("all");
  const [search, setSearch] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  // One row open at a time, held as an identifier rather than an index so it
  // survives a list that reorders under it.
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCustomers(null);
    setLoadFailed(false);
    dataAdapter
      .listPromoOfficeUnlinkedCustomers({
        page,
        pageSize: PAGE_SIZE,
        hasCandidates: FILTER_TO_QUERY[filter],
        q: search.trim() || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setCustomers(response.items);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, page, filter, search, reloadTick]);

  const reload = useCallback(() => {
    setExpandedId(null);
    setReloadTick((n) => n + 1);
    onResolved?.();
  }, [onResolved]);

  // Any change to what is being asked for resets to the first page, or the
  // user lands on page 4 of a two-page result and sees an empty list.
  const changeFilter = (next: CandidateFilter) => {
    setFilter(next);
    setPage(1);
    setExpandedId(null);
  };

  const changeSearch = (next: string) => {
    setSearch(next);
    setPage(1);
    setExpandedId(null);
  };

  if (loadFailed) {
    return (
      <Card className="p-12 text-center space-y-3">
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-600" />
        <p className="text-sm text-muted-foreground">
          The list of customers with no customer in One couldn&apos;t be loaded.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setReloadTick((n) => n + 1)}
        >
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            active={filter === "all"}
            onClick={() => changeFilter("all")}
          >
            All
          </FilterButton>
          {/* The two jobs the list really contains. Confirming a suggestion is
              quick; a customer with none is slow and often ends in creating
              one. Mixed together the quick wins are invisible. */}
          <FilterButton
            active={filter === "suggested"}
            onClick={() => changeFilter("suggested")}
          >
            Has a suggestion
          </FilterButton>
          <FilterButton
            active={filter === "none"}
            onClick={() => changeFilter("none")}
          >
            No suggestion
          </FilterButton>
        </div>
        <SearchBox value={search} onChange={changeSearch} />
      </div>

      {customers === null ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : customers.length === 0 ? (
        <EmptyState filter={filter} searching={search.trim().length > 0} />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {total} {total === 1 ? "customer" : "customers"} with open work and
            no customer in One. Busiest first.
          </p>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-6" />
                  <TableHead>Customer</TableHead>
                  <TableHead>Account no.</TableHead>
                  <TableHead>Postcode</TableHead>
                  <TableHead>Open work</TableHead>
                  <TableHead>Suggestion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <UnlinkedCustomerRow
                    key={customer.promoOfficeCustomerIdentifier}
                    customer={customer}
                    expanded={
                      expandedId === customer.promoOfficeCustomerIdentifier
                    }
                    onToggle={() =>
                      setExpandedId((current) =>
                        current === customer.promoOfficeCustomerIdentifier
                          ? null
                          : customer.promoOfficeCustomerIdentifier,
                      )
                    }
                    onResolved={reload}
                    onCandidateDismissed={() => setReloadTick((n) => n + 1)}
                  />
                ))}
              </TableBody>
            </Table>
            <ListPagination
              page={page}
              totalPages={totalPages}
              onPageChange={(next) => {
                setExpandedId(null);
                setPage(next);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="sm"
      variant={active ? "default" : "outline"}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}

function SearchBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  // Debounced so every keystroke is not a request, but the field itself stays
  // immediately responsive -- a search box that lags behind typing reads as a
  // broken page. Nothing is announced when nothing changed: the committing
  // handler resets the page and collapses the open row, and on mount the
  // draft already equals the value, so an unguarded timer would close a row
  // somebody opened in the first 300ms.
  useEffect(() => {
    if (draft === value) return;
    const timer = setTimeout(() => onChange(draft), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  return (
    <div className="relative w-full sm:w-72">
      <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search customer or account no."
        className="pl-8"
        aria-label="Search customers with no customer in One"
      />
    </div>
  );
}

function EmptyState({
  filter,
  searching,
}: {
  filter: CandidateFilter;
  searching: boolean;
}) {
  const message = searching
    ? "No customer matches that search."
    : filter === "suggested"
      ? "Nothing is waiting on a suggestion being confirmed."
      : filter === "none"
        ? "Every customer here has at least one suggestion to look at."
        : "Every Promo Office customer with open work has a customer in One.";

  return (
    <Card className="p-12 text-center">
      <Check className="w-10 h-10 mx-auto mb-3 text-green-600 dark:text-green-400" />
      <h3 className="mb-1">Nothing to decide</h3>
      <p className="text-muted-foreground text-sm">{message}</p>
    </Card>
  );
}

function UnlinkedCustomerRow({
  customer,
  expanded,
  onToggle,
  onResolved,
  onCandidateDismissed,
}: {
  customer: PromoOfficeUnlinkedCustomer;
  expanded: boolean;
  onToggle: () => void;
  onResolved: () => void;
  onCandidateDismissed: () => void;
}) {
  const topCandidate = customer.candidates[0];
  const extraCandidates = customer.candidates.length - 1;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-medium">
          {customer.promoOfficeCustomerName ?? "—"}
        </TableCell>
        <TableCell className="font-mono text-sm text-muted-foreground">
          {customer.promoOfficeAccountNumber ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {customer.promoOfficePostcode ?? "—"}
        </TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {describeOpenWork(customer) || "—"}
        </TableCell>
        <TableCell className="text-sm">
          {topCandidate ? (
            <span className="text-muted-foreground">
              {topCandidate.cisCustomerName}
              {extraCandidates > 0 && ` and ${extraCandidates} other`}
              {extraCandidates > 1 && "s"}
            </span>
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 p-0">
            <div
              className="p-4 whitespace-normal"
              // Stop the row's own expand/collapse firing when someone uses a
              // button or types in the search field inside the panel.
              onClick={(e) => e.stopPropagation()}
            >
              <ResolutionPanel
                customer={customer}
                onResolved={onResolved}
                onCandidateDismissed={onCandidateDismissed}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ResolutionPanel({
  customer,
  onResolved,
  onCandidateDismissed,
}: {
  customer: PromoOfficeUnlinkedCustomer;
  onResolved: () => void;
  onCandidateDismissed: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const canCreateCustomers = useCanEditCustomers();
  const [busyMatchId, setBusyMatchId] = useState<string | null>(null);
  const [finding, setFinding] = useState(false);
  const [creating, setCreating] = useState(false);

  const openWork = describeOpenWork(customer);

  const confirmLink = async (match: PromoOfficeCustomerMatch) => {
    setBusyMatchId(match.id);
    try {
      await dataAdapter.confirmPromoOfficeCustomerLink(match.id);
      toast.success(
        `${customer.promoOfficeCustomerName ?? "Customer"} linked to ${match.cisCustomerName}`,
      );
      onResolved();
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not link this customer"));
      setBusyMatchId(null);
    }
  };

  const dismissMatch = async (match: PromoOfficeCustomerMatch) => {
    setBusyMatchId(match.id);
    try {
      await dataAdapter.dismissPromoOfficeCustomerMatch(match.id);
      toast.success(`${match.cisCustomerName} rejected as a match`);
      onCandidateDismissed();
    } catch (err) {
      toast.error(
        mutationErrorMessage(err, "Could not reject this suggestion"),
      );
    } finally {
      setBusyMatchId(null);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <section className="space-y-1">
        <h4 className="text-sm font-medium">In Promo Office</h4>
        <p className="text-sm text-muted-foreground">
          {customer.promoOfficeCustomerName ?? "Unnamed customer"}
          {customer.promoOfficePostcode && ` · ${customer.promoOfficePostcode}`}
          {customer.promoOfficeAccountNumber &&
            ` · Account no. ${customer.promoOfficeAccountNumber}`}
        </p>
        {openWork && (
          <p className="text-sm text-muted-foreground">
            {openWork} will be linked by whichever decision you make here.
          </p>
        )}
      </section>

      {customer.candidates.length > 0 ? (
        <section className="space-y-2">
          <h4 className="text-sm font-medium">Suggested customers in One</h4>
          <ul className="space-y-2">
            {customer.candidates.map((candidate) => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                promoOfficePostcode={customer.promoOfficePostcode}
                busy={busyMatchId !== null}
                working={busyMatchId === candidate.id}
                onConfirm={() => confirmLink(candidate)}
                onDismiss={() => dismissMatch(candidate)}
              />
            ))}
          </ul>
        </section>
      ) : (
        <section className="space-y-1">
          <h4 className="text-sm font-medium">No suggestions</h4>
          <p className="text-sm text-muted-foreground">
            Nothing in One matched this customer&apos;s name or postcode. Either
            find the right one by hand, or create it.
          </p>
        </section>
      )}

      <section className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setFinding((open) => !open)}
        >
          <Search className="h-3.5 w-3.5" />
          Find a different customer in One
        </Button>
        {canCreateCustomers && (
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <UserPlus className="h-3.5 w-3.5" />
            Create customer in One
          </Button>
        )}
      </section>

      {finding && (
        <FindCustomerPanel customer={customer} onLinked={onResolved} />
      )}

      <CreateCustomerDialog
        customer={customer}
        open={creating}
        onOpenChange={setCreating}
        onCreated={onResolved}
      />
    </div>
  );
}

function CandidateRow({
  candidate,
  promoOfficePostcode,
  busy,
  working,
  onConfirm,
  onDismiss,
}: {
  candidate: PromoOfficeCustomerMatch;
  promoOfficePostcode: string | null;
  busy: boolean;
  working: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const bothSignals =
    candidate.matchedSignals.includes("name") &&
    candidate.matchedSignals.includes("postcode");

  return (
    <li className="rounded-md border bg-background p-3 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-medium">{candidate.cisCustomerName}</span>
        <span className="text-sm text-muted-foreground">
          {candidate.cisPostcode ?? "no postcode"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        <Badge
          variant={bothSignals ? "success" : "neutral"}
          className="font-normal mr-2"
        >
          {describeMatchedSignals(candidate.matchedSignals)}
        </Badge>
        {/* Both postcodes on one line, because "postcode does not match" is
            only actionable next to the two values it is talking about. */}
        {!bothSignals && (
          <span>
            Promo Office has {promoOfficePostcode ?? "no postcode"}, One has{" "}
            {candidate.cisPostcode ?? "no postcode"}.
          </span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={busy} onClick={onConfirm}>
          {working ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Confirm
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={onDismiss}>
          <X className="h-3.5 w-3.5" />
          Not a match
        </Button>
        {/* Says the recovery before anyone needs it. The suggestion never
            comes back, but linking to this customer by hand still works, so
            this is not the warning a destructive action would earn. */}
        <span className="text-xs text-muted-foreground">
          Stops this suggestion coming back. You can still link to this customer
          by hand.
        </span>
      </div>
    </li>
  );
}

function FindCustomerPanel({
  customer,
  onLinked,
}: {
  customer: PromoOfficeUnlinkedCustomer;
  onLinked: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [term, setTerm] = useState(customer.promoOfficeCustomerName ?? "");
  const [results, setResults] = useState<PromoOfficeCisCustomer[] | null>(null);
  const [searchFailed, setSearchFailed] = useState<string | null>(null);
  const [linkingCode, setLinkingCode] = useState<string | null>(null);
  const requestId = useRef(0);

  const trimmed = term.trim();

  useEffect(() => {
    if (trimmed.length === 0) {
      setResults(null);
      setSearchFailed(null);
      return;
    }
    const mine = ++requestId.current;
    const timer = setTimeout(() => {
      setSearchFailed(null);
      dataAdapter
        .searchPromoOfficeCisCustomers(trimmed)
        .then((found) => {
          // Only the newest search may write. Without this a slow early
          // request can land after a later one and replace good results with
          // stale ones.
          if (mine === requestId.current) setResults(found);
        })
        .catch((err) => {
          if (mine !== requestId.current) return;
          setResults([]);
          setSearchFailed(
            mutationErrorMessage(
              err,
              "One couldn't be searched just now. Try again in a moment.",
            ),
          );
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [dataAdapter, trimmed]);

  const link = async (found: PromoOfficeCisCustomer) => {
    setLinkingCode(found.customerId);
    try {
      await dataAdapter.linkPromoOfficeCustomerToExisting({
        promoOfficeCustomerIdentifier: customer.promoOfficeCustomerIdentifier,
        customerId: found.customerId,
        promoOfficeCustomerName: customer.promoOfficeCustomerName,
      });
      toast.success(
        `${customer.promoOfficeCustomerName ?? "Customer"} linked to ${found.cisCustomerCode}`,
      );
      onLinked();
    } catch (err) {
      toast.error(mutationErrorMessage(err, "Could not link this customer"));
      setLinkingCode(null);
    }
  };

  return (
    <section className="rounded-md border bg-background p-3 space-y-3">
      <div className="space-y-1">
        <Label htmlFor="cis-customer-search">Search One by name</Label>
        <Input
          id="cis-customer-search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Customer name"
        />
        <p className="text-xs text-muted-foreground">
          Searches One directly, so a customer created minutes ago is already
          here.
        </p>
      </div>

      {searchFailed && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {searchFailed}
        </p>
      )}

      {results !== null && results.length === 0 && !searchFailed && (
        <p className="text-sm text-muted-foreground">
          No customer in One matches &ldquo;{trimmed}&rdquo;.
        </p>
      )}

      {results !== null && results.length > 0 && (
        <ul className="divide-y rounded-md border">
          {results.map((found) => (
            <li
              key={found.customerId}
              className="flex flex-wrap items-center justify-between gap-2 p-2"
            >
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-sm">
                  {found.cisCustomerCode}
                </span>
                <span className="text-sm">{found.name}</span>
                <span className="text-xs text-muted-foreground">
                  {found.postcode ?? "no postcode"}
                </span>
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={linkingCode !== null}
                onClick={() => link(found)}
              >
                {linkingCode === found.customerId && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Link
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * "Create a customer in One" (v1 completion C5, mock-up state 10): the "New
 * customer" form's name, sales rep and CSR, the name filled from Promo
 * Office. On create, the Promo Office customer is linked to it.
 */
function CreateCustomerDialog({
  customer,
  open,
  onOpenChange,
  onCreated,
}: {
  customer: PromoOfficeUnlinkedCustomer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const idPrefix = useId();
  const promoName = customer.promoOfficeCustomerName ?? "";
  const [form, setForm] = useState<NewCustomerForm | null>(null);
  const [draft, setDraft] = useState<NewCustomerDraft>(() =>
    emptyNewCustomerDraft(null, promoName),
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<
    Partial<Record<NewCustomerField, string>>
  >({});
  const [failure, setFailure] = useState<string | null>(null);

  // Re-seed whenever the dialog opens, or a name edited and abandoned on one
  // customer would be sitting in the box for the next.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDraft(emptyNewCustomerDraft(null, promoName));
    setErrors({});
    setFailure(null);
    dataAdapter
      .getNewCustomerForm()
      .then((loaded) => {
        if (cancelled) return;
        setForm(loaded);
        setDraft((current) => ({
          ...current,
          salesRepUserId: loaded.default_sales_rep_user_id ?? "",
          csrUserId: loaded.default_csr_user_id ?? "",
        }));
      })
      .catch(() => {
        // Without the lists the owners stay House Account; the create still works.
      });
    return () => {
      cancelled = true;
    };
  }, [open, promoName, dataAdapter]);

  const create = async () => {
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const owners = newCustomerOwners(draft);
      await dataAdapter.linkPromoOfficeCustomerToNew({
        promoOfficeCustomerIdentifier: customer.promoOfficeCustomerIdentifier,
        companyName: draft.name,
        salesRepUserId: owners.sales_rep_user_id,
        csrUserId: owners.csr_user_id,
      });
      toast.success("Customer created");
      onOpenChange(false);
      onCreated();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const bound = bindNewCustomerErrors(fieldErrors);
        setErrors(bound.byField);
        setFailure([fieldsNeedAFix(bound.count), ...bound.unbound].join(" "));
      } else {
        setFailure(
          mutationErrorMessage(err, "The customer wasn't created. Try again."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create a customer in One</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          {/* The one action on this page that cannot be undone from here.
              Said in business terms, not "this is irreversible". */}
          <p className="text-sm text-muted-foreground">
            Unlinking afterwards leaves the new customer behind: check it does
            not already exist under another name first.
          </p>
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
            >
              {failure}
            </p>
          )}
          {form && wontSeeNewCustomer(form, draft) && <NotInPortfolioNotice />}
          <NewCustomerFields
            idPrefix={idPrefix}
            form={form}
            draft={draft}
            onDraftChange={setDraft}
            errors={errors}
            disabled={saving}
            showMarket={false}
            showAddress={false}
          />
          <p className="text-xs text-muted-foreground">
            Linked to Promo Office customer {promoName || "this customer"} once
            created.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create and link
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
